import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';

/**
 * Detects if the current OS is Windows
 */
const isWindows = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  return navigator.platform.toLowerCase().includes('win') || 
         navigator.userAgent.toLowerCase().includes('windows');
};

interface WindowsScrollArrowsProps {
  children: React.ReactNode;
  /** Additional className for the container */
  className?: string;
  /** Custom scroll amount per click (default: 200px) */
  scrollAmount?: number;
  /** Show arrows even on non-Windows (for testing) */
  forceShow?: boolean;
  /** Show arrows on narrow viewports (< 500px) regardless of platform */
  forceShowOnNarrow?: boolean;
}

/**
 * A wrapper component that adds left/right scroll arrows for horizontal scrollable content.
 * Only shows arrows on Windows OS (Mac/mobile users can swipe/scroll naturally).
 * 
 * Usage:
 * <WindowsScrollArrows>
 *   <div className="flex overflow-x-auto">
 *     {items.map(...)}
 *   </div>
 * </WindowsScrollArrows>
 */
const WindowsScrollArrows: React.FC<WindowsScrollArrowsProps> = ({
  children,
  className = '',
  scrollAmount = 200,
  forceShow = false,
  forceShowOnNarrow = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const [isWindowsOS, setIsWindowsOS] = useState(false);
  const [isNarrowViewport, setIsNarrowViewport] = useState(false);

  // Detect Windows OS on mount
  useEffect(() => {
    setIsWindowsOS(isWindows());
  }, []);

  // Detect narrow viewport for forceShowOnNarrow
  useEffect(() => {
    if (!forceShowOnNarrow) return;

    const checkViewportWidth = () => {
      setIsNarrowViewport(window.innerWidth < 500);
    };

    checkViewportWidth();
    window.addEventListener('resize', checkViewportWidth);
    return () => window.removeEventListener('resize', checkViewportWidth);
  }, [forceShowOnNarrow]);

  // Check scroll position and update arrow visibility
  const updateArrowVisibility = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    // Find the scrollable element (first child with overflow-x-auto or the container itself)
    const scrollable = container.querySelector('.overflow-x-auto, [style*="overflow-x"]') as HTMLElement || container;
    
    if (scrollable) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollable;
      const hasOverflow = scrollWidth > clientWidth;
      
      setShowLeftArrow(hasOverflow && scrollLeft > 5);
      setShowRightArrow(hasOverflow && scrollLeft < scrollWidth - clientWidth - 5);
    }
  }, []);

  // Set up scroll listener and initial check
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const scrollable = container.querySelector('.overflow-x-auto, [style*="overflow-x"]') as HTMLElement || container;
    
    // Initial check
    updateArrowVisibility();

    // Listen to scroll events
    scrollable.addEventListener('scroll', updateArrowVisibility);
    
    // Listen to resize events (content might change)
    const resizeObserver = new ResizeObserver(updateArrowVisibility);
    resizeObserver.observe(scrollable);

    // Also check after a short delay (for dynamic content)
    const timeout = setTimeout(updateArrowVisibility, 100);

    return () => {
      scrollable.removeEventListener('scroll', updateArrowVisibility);
      resizeObserver.disconnect();
      clearTimeout(timeout);
    };
  }, [updateArrowVisibility, children]);

  // Handle scroll button clicks
  const handleScroll = (direction: 'left' | 'right') => {
    const container = containerRef.current;
    if (!container) return;

    const scrollable = container.querySelector('.overflow-x-auto, [style*="overflow-x"]') as HTMLElement || container;
    
    if (scrollable) {
      const newScrollLeft = direction === 'left' 
        ? scrollable.scrollLeft - scrollAmount 
        : scrollable.scrollLeft + scrollAmount;
      
      scrollable.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  // Don't show arrows on non-Windows (unless forced for testing or narrow viewport)
  const shouldShowArrows = forceShow || isWindowsOS || (forceShowOnNarrow && isNarrowViewport);

  if (!shouldShowArrows) {
    // On Mac/mobile, just render children as-is
    return <>{children}</>;
  }

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      {/* Left fade + subtle arrow */}
      {showLeftArrow && (
        <button
          onClick={() => handleScroll('left')}
          aria-label="Scroll left"
          type="button"
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: '32px',
            zIndex: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(to right, #FFFFFF 40%, transparent 100%)',
            border: 'none',
            cursor: 'pointer',
            padding: 0
          }}
        >
          <ChevronLeftIcon style={{ width: '20px', height: '20px', color: 'var(--text-secondary)' }} />
        </button>
      )}

      {/* Content */}
      {children}

      {/* Right fade + subtle arrow */}
      {showRightArrow && (
        <button
          onClick={() => handleScroll('right')}
          aria-label="Scroll right"
          type="button"
          style={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: '32px',
            zIndex: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(to left, #FFFFFF 40%, transparent 100%)',
            border: 'none',
            cursor: 'pointer',
            padding: 0
          }}
        >
          <ChevronRightIcon style={{ width: '20px', height: '20px', color: 'var(--text-secondary)' }} />
        </button>
      )}
    </div>
  );
};

export default WindowsScrollArrows;

